/**
 * User profile screen (route: UserProfile, param: username).
 *
 * Renders another user's profile header (banner, avatar, bio, stats, follow /
 * message / more actions) above a FlatList of their posts.
 *
 * MIGRATION (bare RN, P3-6): ported off Expo —
 *   expo-router        -> @react-navigation (useNavigation/route.params; the
 *                         dynamic header title is set via navigation.setOptions
 *                         instead of <Stack.Screen options>).
 *   @expo/vector-icons -> components/ui/Icon shim.
 *   expo-linear-gradient -> react-native-linear-gradient.
 * Services (api, socialGraph) + the A15 ReportBlockSheet + the live follow-count
 * logic are preserved verbatim.
 *
 * API endpoints:
 *   GET    /users/profile/:username   — profile (see normalizeProfile)
 *   GET    /users/:username/posts     — { items: [...] }, see normalizePost
 *   POST   / DELETE /users/:username/follow — follow / unfollow
 *   POST   /users/:username/block     — block (then navigate back)
 *   POST   /users/:username/report    — report
 *   POST   /posts/:id/like            — like from a post card (fire-and-forget)
 *   POST   /posts/:id/repost          — repost from a post card (fire-and-forget)
 *
 * Key behavior:
 *   - Follow is optimistic WITH rollback: the flag + follower count flip
 *     immediately and revert if the request fails.
 *   - Post-card like/repost buttons fire one-shot POSTs with no local state
 *     update (counts only refresh on a full reload).
 *   - Block/report use the shared glass ReportBlockSheet (A15).
 */
import { useEffect, useLayoutEffect, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  useWindowDimensions,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { Ionicons } from '../components/ui/Icon';
import RichPostText from '../components/feed/RichPostText';
import { navigate as navRef } from '../navigation/navigationRef';
import LinearGradient from 'react-native-linear-gradient';
import { api, getMe } from '../services/api';
import { seedEngagement, subscribe as subscribeEngagement, applyLike, applyRepost, getEngagement } from '../services/postEngagement';
import { colors, spacing, radius, fonts, typographyScale, shadowTokens, getLayout } from '../constants/theme';
import { GlassSurface, glassTokens } from '../components/glass';
import ParticleBackground from '../components/ui/ParticleBackground';
import LoadingState from '../components/ui/LoadingState';
import ReportBlockSheet, { ReportTarget } from '../components/moderation/ReportBlockSheet';
import { friendlyDisplayName } from '../lib/identity';
import { tabularNums } from '../lib/format';
import { setFollow, seedCounts, seedFollowState, subscribeCounts } from '../services/socialGraph';
import { usePresence, formatLastSeen } from '../services/presence';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList, 'UserProfile'>;
type Rt = RouteProp<RootStackParamList, 'UserProfile'>;

interface UserProfile {
  id: string;
  username: string;
  displayName: string;
  avatar: string;
  banner: string;
  bio: string;
  followers: number;
  following: number;
  posts: number;
  isFollowing?: boolean;
  verified?: boolean;
  joinedAt: string;
}

interface PostItem {
  id: string;
  content: string;
  createdAt: string;
  likes: number;
  comments: number;
  reposts: number;
  liked?: boolean;
  reposted?: boolean;
}

// GET /users/profile/:username returns { id, username, displayName, avatar, bio,
// joinedAt, isFollowing, followersCount, followingCount, postsCount, isVerified? }.
function normalizeProfile(raw: any): UserProfile {
  return {
    id: raw?.id ?? '',
    username: raw?.username ?? '',
    displayName: raw?.displayName ?? raw?.display_name ?? '',
    avatar: raw?.avatar ?? raw?.avatar_url ?? '',
    banner: raw?.banner ?? raw?.banner_url ?? '',
    bio: raw?.bio ?? '',
    followers: raw?.followersCount ?? raw?.followers ?? 0,
    following: raw?.followingCount ?? raw?.following ?? 0,
    posts: raw?.postsCount ?? raw?.posts ?? 0,
    isFollowing: raw?.isFollowing ?? false,
    verified: raw?.isVerified ?? raw?.verified ?? false,
    joinedAt: raw?.joinedAt ?? raw?.createdAt ?? '',
  };
}

// GET /users/:username/posts returns { data: { items: [...] } }, each post with
// _count.Vote/_count.Comment + repostCount (camelCase).
function normalizePost(raw: any): PostItem {
  return {
    id: raw?.id,
    content: raw?.content ?? '',
    createdAt: raw?.createdAt ?? raw?.created_at ?? '',
    likes: raw?._count?.Vote ?? raw?.upvotes ?? 0,
    comments: raw?._count?.Comment ?? raw?.commentCount ?? 0,
    reposts: raw?.repostCount ?? 0,
    liked: raw?.liked ?? false,
    reposted: raw?.reposted ?? false,
  };
}

export default function UserProfileScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<Rt>();
  const username = route.params?.username;
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;

  // --- State ---
  const [user, setUser] = useState<UserProfile | null>(null);
  // True when the profile being viewed is the current user's own (resolved from
  // getMe() in fetchProfile, same identity check as the self-CALL guard below).
  // When self: render "Edit Profile" instead of Follow/Message/Call, and the
  // follow/message handlers early-return so you can't act on yourself.
  const [isSelf, setIsSelf] = useState(false);
  // Live presence for this profile (online dot + status line). Undefined until the
  // first socket presence update for this user lands.
  const presence = usePresence(user?.id);
  const [posts, setPosts] = useState<PostItem[]>([]);
  const [loading, setLoading] = useState(true);
  // Distinguish a transient network/server FAILURE (retryable) from a genuine
  // 404 "user not found" — the old code showed "not found" for both.
  const [error, setError] = useState(false);
  // Which start-call request is in flight ('audio' | 'video'), or null. Doubles
  // as the double-tap guard AND drives the spinner on the matching button.
  const [calling, setCalling] = useState<'audio' | 'video' | null>(null);

  // --- Effects ---
  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  // Apple skill §12: hide the opaque native stack header and render our own
  // translucent in-screen header instead, so the aurora shows through the notch
  // and content scrolls UNDER the bar (no fixed opaque strip). The dynamic title
  // (friendlyDisplayName — never an auto-generated `d967bbgzv2` handle, Triage P0
  // identity IMG_5938) now lives in that in-screen header below.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  // LR4: subscribe to live count changes for THIS profile. When someone follows/
  // unfollows this user (from any device, including a two-user live test), the
  // server emits follow:update → the store reconciles → we patch the rendered
  // follower/following counts with the AUTHORITATIVE numbers (no drift).
  useEffect(() => {
    if (!user?.username) return;
    const unsub = subscribeCounts({ id: user.id, username: user.username }, (c) => {
      setUser((prev) => {
        if (!prev) return prev;
        if (prev.followers === c.followerCount && prev.following === c.followingCount) return prev;
        return { ...prev, followers: c.followerCount, following: c.followingCount };
      });
    });
    return unsub;
  }, [user?.id, user?.username]);

  // Load profile + posts in parallel; failure leaves `user` null → "not found".
  async function fetchProfile() {
    setLoading(true);
    setError(false);
    try {
      const [userRes, postsRes] = await Promise.all([
        api<any>(`/users/profile/${encodeURIComponent(username)}`),
        api<{ items?: any[] }>(`/users/${encodeURIComponent(username)}/posts`),
      ]);
      if (userRes.success && userRes.data) {
        const np = normalizeProfile(userRes.data);
        setUser(np);
        // Resolve the current user and flag if this IS our own profile — the
        // UserProfile route can land on yourself (e.g. tapping your own @handle),
        // and Follow/Message/Call don't make sense there. Same identity check the
        // self-call guard uses (getMe id === profile id); /auth/me wraps as
        // { user: {...} } so unwrap one level.
        try {
          const meRes = await getMe();
          const me = (meRes as any)?.data?.user ?? (meRes as any)?.data ?? null;
          setIsSelf(!!me?.id && me.id === np.id);
        } catch { /* can't resolve me → treat as not-self; backend still guards */ }
        // LR4: seed the live count store + local follow state so socket
        // follow:update events (from this or another device) reconcile B's
        // follower count here without a refetch.
        seedCounts({ id: np.id, username: np.username }, {
          followerCount: np.followers,
          followingCount: np.following,
          isFollowing: np.isFollowing,
        });
        seedFollowState({ id: np.id, username: np.username }, !!np.isFollowing);
      }
      if (postsRes.success && postsRes.data) {
        const arr = Array.isArray(postsRes.data?.items) ? postsRes.data.items
          : Array.isArray((postsRes.data as any)?.posts) ? (postsRes.data as any).posts
          : Array.isArray(postsRes.data) ? postsRes.data : [];
        setPosts(arr.map(normalizePost));
      }
    } catch {
      // Transient network/server failure → retryable error state (NOT "not found").
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  // --- Handlers ---
  // LR4: optimistic follow/unfollow via the social-graph service. The flag +
  // follower count flip immediately; setFollow() reconciles to the server's
  // AUTHORITATIVE count (count-drift guard), rolls back on failure, propagates
  // the followed user's posts into the live feed, and the follow:update socket
  // event keeps the other user's device + our own other devices in sync.
  const [followBusy, setFollowBusy] = useState(false);
  async function handleFollow() {
    if (!user || followBusy || isSelf) return;
    const was = !!user.isFollowing;
    // Optimistic UI.
    setUser((prev) => prev ? {
      ...prev,
      isFollowing: !was,
      followers: Math.max(0, (prev.followers ?? 0) + (was ? -1 : 1)),
    } : prev);
    setFollowBusy(true);
    try {
      const res = await setFollow({ id: user.id, username: user.username }, !was);
      if (!res.success) {
        // Rollback on failure.
        setUser((prev) => prev ? {
          ...prev,
          isFollowing: was,
          followers: Math.max(0, (prev.followers ?? 0) + (was ? 1 : -1)),
        } : prev);
      } else if (typeof res.followerCount === 'number') {
        // Reconcile to the authoritative follower count from the server reply.
        setUser((prev) => prev ? { ...prev, isFollowing: !was, followers: res.followerCount! } : prev);
      }
    } finally {
      setFollowBusy(false);
    }
  }

  // Open (or create) a DM thread with THIS user (was dropping the recipient →
  // a blank picker). DMNew accepts an optional { username } to preselect them.
  function handleMessage() {
    if (isSelf) return;
    navigation.navigate('DMNew', username ? { username } : undefined);
  }

  // Start a 1:1 audio OR video call with this user. POSTs /voice/video/call
  // (which also rings the peer via Socket.IO), then opens the full-screen call
  // screen with the returned LiveKit token/url. The backend derives the ring's
  // media kind from `videoEnabled` (no separate `media` body field) and carries
  // it to the callee (socket incomingVideoCall.callType + push data.media), so
  // audio calls send videoEnabled:false and the same kind rides into the
  // VoiceCall params. Double-tap guarded; crash-safe.
  async function handleCall(media: 'audio' | 'video') {
    if (calling || !user) return;
    if (!user.id) {
      Alert.alert('Unavailable', 'Could not start a call with this user.');
      return;
    }
    // Guard: you can't call yourself — a self-call never connects and left the
    // caller stuck on the ringing screen with no clear exit.
    try {
      const me = await getMe();
      if ((me as any)?.id && (me as any).id === user.id) {
        Alert.alert("That's you", "You can't start a call with yourself.");
        return;
      }
    } catch { /* if we can't resolve me, fall through — backend still guards */ }
    setCalling(media);
    try {
      const res = await api<any>('/voice/video/call', {
        method: 'POST',
        body: JSON.stringify({
          targetUserId: user.id,
          callType: 'direct',
          videoEnabled: media === 'video',
          audioEnabled: true,
        }),
      });
      const token = res.data?.token;
      const url = res.data?.liveKitUrl || res.data?.url;
      if (res.success && token && url) {
        navigation.navigate('VoiceCall', {
          callId: res.data?.callId ?? '',
          token,
          url,
          media,
          roomName: res.data?.roomName ?? '',
          peerName: friendlyDisplayName(user.displayName, user.username),
          peerAvatar: user.avatar || '',
          isCaller: true,
        });
      } else if ((res as any).code === 'USER_BUSY' || (res as any).data?.busy || /busy/i.test(res.error || '')) {
        // Callee is already on another call (server 409). LR8 BUSY signal.
        Alert.alert('Busy', `${friendlyDisplayName(user.displayName, user.username)} is on another call right now.`);
      } else {
        Alert.alert('Call failed', res.error || 'Could not start the call. Please try again.');
      }
    } catch {
      Alert.alert('Call failed', 'Could not start the call. Please try again.');
    } finally {
      setCalling(null);
    }
  }

  // Trust & Safety (Apple 1.2): report or block this account. Opens the shared
  // glass ReportBlockSheet (reason picker + block/mute + confirmation). After a
  // block we leave the now-inaccessible profile.
  const [tsTarget, setTsTarget] = useState<ReportTarget | null>(null);
  function handleMoreMenu() {
    setTsTarget({
      type: 'user',
      authorId: user?.id || undefined,
      authorUsername: username || user?.username || undefined,
      authorLabel: `@${username || user?.username || 'user'}`,
    });
  }

  // --- Helpers / render ---
  // Compact relative timestamp (now / 12m / 5h / 3d); '' for missing/bad dates.
  function formatTime(dateStr?: string | null): string {
    if (!dateStr) return '';
    const ts = new Date(dateStr).getTime();
    if (isNaN(ts)) return '';
    const diff = Date.now() - ts;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  }

  // List header: full profile card (banner, avatar, actions, bio, stats).
  function renderHeader() {
    if (!user) return null;
    return (
      <View>
        {/* Banner */}
        <View style={styles.bannerContainer}>
          {user.banner && user.banner.length > 0 ? (
            <CachedImage source={{ uri: user.banner }} style={styles.banner} resizeMode="cover" />
          ) : (
            // Default banner: brand lavender gradient (the primary→ink mix read
            // as a dark slab against the lavender system — Triage IMG_5939 class).
            <LinearGradient
              colors={colors.gradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.banner}
            />
          )}
        </View>

        <View style={styles.profileSection}>
          {/* Avatar + action buttons row */}
          <View style={styles.avatarActionsRow}>
            <View>
              {user.avatar && user.avatar.length > 0 ? (
                <CachedImage source={{ uri: user.avatar }} style={styles.avatar} />
              ) : (
                <View style={[styles.avatar, styles.avatarFallback]}>
                  <Text style={styles.avatarLetter}>{friendlyDisplayName(user.displayName, user.username).charAt(0).toUpperCase()}</Text>
                </View>
              )}
              {presence?.online && <View style={styles.profileOnlineDot} />}
            </View>
            <View style={styles.actionButtons}>
              {isSelf ? (
                /* Viewing our OWN profile via the UserProfile route: Follow /
                   Message / Call don't apply — offer "Edit Profile" instead. */
                <TouchableOpacity
                  style={styles.editProfileBtn}
                  onPress={() => navigation.navigate('EditProfile')}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel="Edit profile"
                >
                  <Text style={styles.editProfileBtnText}>Edit Profile</Text>
                </TouchableOpacity>
              ) : (
              <>
              {/* "More" now lives in the translucent top bar (skill §16 restraint). */}
              <GlassSurface
                variant="card"
                radius={glassTokens.radius.pill}
                style={styles.iconBtn}
                contentStyle={styles.iconBtnContent}
                onPress={handleMessage}
                accessibilityLabel="Send message"
                hitSlop={8}
              >
                <Ionicons name="chatbubble-outline" size={18} color={colors.primary} />
              </GlassSurface>
              {/* 📞 Audio call (videoEnabled:false → backend rings media:'audio'). */}
              <GlassSurface
                variant="card"
                radius={glassTokens.radius.pill}
                style={[styles.iconBtn, !!calling && styles.callBtnDisabled]}
                contentStyle={styles.iconBtnContent}
                onPress={calling ? undefined : () => handleCall('audio')}
                disabled={!!calling}
                accessibilityLabel={`Voice call ${user.username}`}
                hitSlop={8}
              >
                {calling === 'audio' ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <Ionicons name="call-outline" size={18} color={colors.primary} />
                )}
              </GlassSurface>
              {/* 🎥 Video call (videoEnabled:true → backend rings media:'video'). */}
              <GlassSurface
                variant="card"
                radius={glassTokens.radius.pill}
                style={[styles.iconBtn, !!calling && styles.callBtnDisabled]}
                contentStyle={styles.iconBtnContent}
                onPress={calling ? undefined : () => handleCall('video')}
                disabled={!!calling}
                accessibilityLabel={`Video call ${user.username}`}
                hitSlop={8}
              >
                {calling === 'video' ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <Ionicons name="videocam-outline" size={18} color={colors.primary} />
                )}
              </GlassSurface>
              <TouchableOpacity
                style={[styles.followBtn, user.isFollowing && styles.followingBtn]}
                onPress={handleFollow}
                disabled={followBusy}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityLabel={user.isFollowing ? `Unfollow ${user.username}` : `Follow ${user.username}`}
                accessibilityState={{ disabled: followBusy }}
              >
                <Text style={[styles.followBtnText, user.isFollowing && styles.followingBtnText]}>
                  {user.isFollowing ? 'Following' : 'Follow'}
                </Text>
              </TouchableOpacity>
              </>
              )}
            </View>
          </View>

          {/* Name */}
          <View style={styles.nameRow}>
            <Text style={styles.displayName} numberOfLines={1}>{friendlyDisplayName(user.displayName, user.username)}</Text>
            {user.verified && (
              <Ionicons name="checkmark-circle" size={18} color={colors.primary} />
            )}
          </View>
          <Text style={styles.username} numberOfLines={1}>@{user.username || 'user'}</Text>

          {/* Live presence status (online / last-seen) */}
          {presence ? (
            <View style={styles.presenceRow}>
              <View style={[styles.presenceDot, { backgroundColor: presence.online ? colors.success : colors.faint }]} />
              <Text style={styles.presenceText}>
                {presence.online ? 'Online' : formatLastSeen(presence.lastSeen)}
              </Text>
            </View>
          ) : null}

          {/* Bio */}
          {user.bio ? <Text style={styles.bio} numberOfLines={5}>{user.bio}</Text> : null}

          {/* Joined date */}
          {user.joinedAt ? (
            <View style={styles.joinedRow}>
              <Ionicons name="calendar-outline" size={14} color={colors.muted} />
              <Text style={styles.joinedText}>
                Joined{' '}
                {(() => {
                  const d = new Date(user.joinedAt);
                  return isNaN(d.getTime())
                    ? ''
                    : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                })()}
              </Text>
            </View>
          ) : null}

          {/* Stats — glass stat cards, consistent with the own-profile screen. */}
          <View style={styles.statsRow}>
            <GlassSurface
              variant="card"
              radius={glassTokens.radius.md}
              style={styles.statItem}
              contentStyle={styles.statContent}
              accessibilityLabel="View following"
              onPress={() => navigation.navigate('FollowList', { username: user.username, mode: 'following' })}
            >
              <Text style={styles.statNumber}>{(user.following ?? 0).toLocaleString()}</Text>
              <Text style={styles.statLabel}>Following</Text>
            </GlassSurface>
            <GlassSurface
              variant="card"
              radius={glassTokens.radius.md}
              style={styles.statItem}
              contentStyle={styles.statContent}
              accessibilityLabel="View followers"
              onPress={() => navigation.navigate('FollowList', { username: user.username, mode: 'followers' })}
            >
              <Text style={styles.statNumber}>{(user.followers ?? 0).toLocaleString()}</Text>
              <Text style={styles.statLabel}>Followers</Text>
            </GlassSurface>
            <GlassSurface variant="card" radius={glassTokens.radius.md} style={styles.statItem} contentStyle={styles.statContent}>
              <Text style={styles.statNumber}>{(user.posts ?? 0).toLocaleString()}</Text>
              <Text style={styles.statLabel}>Posts</Text>
            </GlassSurface>
          </View>
        </View>

        {/* Posts header */}
        <View style={styles.postsHeader}>
          <Text style={styles.postsHeaderText}>Posts</Text>
        </View>
      </View>
    );
  }

  // Post card. Author identity is the profile `user` (this is their own feed).
  // Delegates to the module-level UserPostCard which wires like/repost to the
  // shared postEngagement store (optimistic toggle + real DELETE/POST + live
  // heart fill), fixing the old fire-and-forget buttons that never reflected.
  function renderPostItem({ item }: { item: PostItem }) {
    return (
      <UserPostCard
        item={item}
        owner={user}
        time={formatTime(item.createdAt)}
        onOpen={() => navigation.navigate('Post', { id: item.id })}
      />
    );
  }

  // Compact translucent in-screen header (skill §12): back chevron + centered
  // title + a trailing "more" affordance, floating over the aurora. Content
  // scrolls under it. Shared across loading / not-found / loaded states so the
  // chrome never jumps.
  function renderTopBar(title: string, showMore: boolean) {
    return (
      <GlassSurface
        variant="bar"
        mode="light"
        radius={0}
        surfaceColor="transparent"
        style={[styles.topBar, styles.topBarTransparent]}
        contentStyle={[styles.topBarContent, { paddingTop: insets.top + spacing.xs }]}
      >
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Ionicons name="chevron-back" size={24} color={colors.ink} />
        </TouchableOpacity>
        <Text style={styles.topBarTitle} numberOfLines={1}>{title}</Text>
        {showMore ? (
          <TouchableOpacity
            onPress={handleMoreMenu}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityRole="button"
            accessibilityLabel="More options"
          >
            <Ionicons name="ellipsis-horizontal" size={22} color={colors.ink} />
          </TouchableOpacity>
        ) : (
          <View style={styles.topBarSpacer} />
        )}
      </GlassSurface>
    );
  }

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={5} />
        {renderTopBar('Profile', false)}
        <LoadingState message="Loading…" onRetry={fetchProfile} />
      </View>
    );
  }

  if (!user) {
    // Retryable failure vs genuine 404: error → "Couldn't load" + Retry;
    // otherwise → "User not found" + Go back.
    return (
      <View style={styles.screen}>
        <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={5} />
        {renderTopBar('Profile', false)}
        <View style={styles.loaderBody}>
          <Text style={styles.errorText}>{error ? "Couldn't load profile" : 'User not found'}</Text>
          {error ? (
            <TouchableOpacity onPress={fetchProfile} accessibilityRole="button" accessibilityLabel="Retry">
              <Text style={styles.backLink}>Retry</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity onPress={() => navigation.goBack()} accessibilityRole="button" accessibilityLabel="Go back">
              <Text style={styles.backLink}>Go back</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      {/* Full-bleed aurora under the status bar + translucent header (skill §12). */}
      <ParticleBackground style={StyleSheet.absoluteFill} theme="light" count={5} />
      {renderTopBar(friendlyDisplayName(user.displayName, user.username), !isSelf)}
      <FlatList
        data={Array.isArray(posts) ? posts : []}
        keyExtractor={(item, index) => item?.id ?? String(index)}
        renderItem={renderPostItem}
        ListHeaderComponent={renderHeader}
        contentContainerStyle={[styles.listContent, containerStyle]}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          <Text style={styles.emptyText}>No posts yet.</Text>
        }
      />
      <ReportBlockSheet
        visible={!!tsTarget}
        target={tsTarget}
        onClose={() => setTsTarget(null)}
        onBlocked={() => { setTsTarget(null); navigation.goBack(); }}
      />
    </View>
  );
}

// Post card wired to the shared postEngagement store: optimistic like/repost
// toggle (fills the heart instantly, real DELETE/POST, rollback on failure) so
// interactions actually reflect + persist — replaces the old fire-and-forget.
function UserPostCard({ item, owner, time, onOpen }: { item: PostItem; owner: any; time: string; onOpen: () => void }) {
  const [eng, setEng] = useState(() =>
    getEngagement(item.id) ?? seedEngagement(item.id, {
      liked: item.liked ?? false, likes: item.likes ?? 0,
      reposted: item.reposted ?? false, reposts: item.reposts ?? 0, bookmarked: false,
    })
  );
  useEffect(() => {
    seedEngagement(item.id, {
      liked: item.liked ?? false, likes: item.likes ?? 0,
      reposted: item.reposted ?? false, reposts: item.reposts ?? 0,
    });
    return subscribeEngagement(item.id, setEng);
  }, [item.id]);

  const onLike = () => {
    const was = eng.liked;
    applyLike(item.id, !was); // optimistic + notifies subscribers
    api(`/posts/${item.id}/like`, { method: was ? 'DELETE' : 'POST' }).then((r) => { if (!r.success) applyLike(item.id, was); });
  };
  const onRepost = () => {
    const was = eng.reposted;
    applyRepost(item.id, !was);
    api(`/posts/${item.id}/repost`, { method: was ? 'DELETE' : 'POST' }).then((r) => { if (!r.success) applyRepost(item.id, was); });
  };

  return (
    <GlassSurface
      variant="card"
      radius={glassTokens.radius.lg}
      style={styles.postCard}
      contentStyle={styles.postCardContent}
      onPress={onOpen}
      accessibilityLabel="Open post"
    >
      <View style={styles.postCardHeader}>
        {owner?.avatar && owner.avatar.length > 0 ? (
          <CachedImage source={{ uri: owner.avatar }} style={styles.postAvatar} />
        ) : (
          <View style={[styles.postAvatar, styles.avatarFallback]}>
            <Text style={styles.postAvatarLetter}>{friendlyDisplayName(owner?.displayName, owner?.username).charAt(0).toUpperCase()}</Text>
          </View>
        )}
        <View style={{ flex: 1 }}>
          <Text style={styles.postName} numberOfLines={1}>{friendlyDisplayName(owner?.displayName, owner?.username)}</Text>
          <Text style={styles.postHandle} numberOfLines={1}>@{owner?.username || 'user'}</Text>
        </View>
        <Text style={styles.postTime}>{time}</Text>
      </View>
      <RichPostText content={item.content || ''} style={styles.postContent} numberOfLines={4} onCashtag={(symbol) => navRef('CoinDetail', { symbol })} />
      <View style={styles.postActions}>
        <TouchableOpacity style={styles.postActionItem} onPress={onOpen} accessibilityRole="button" accessibilityLabel="View comments">
          <Ionicons name="chatbubble-outline" size={14} color={colors.muted} />
          <Text style={styles.postActionText}>{item.comments ?? 0}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.postActionItem} onPress={onRepost} accessibilityRole="button" accessibilityLabel={eng.reposted ? 'Undo repost' : 'Repost'}>
          <Ionicons name="repeat-outline" size={16} color={eng.reposted ? colors.success : colors.muted} />
          <Text style={[styles.postActionText, eng.reposted && { color: colors.success }]}>{eng.reposts}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.postActionItem} onPress={onLike} accessibilityRole="button" accessibilityLabel={eng.liked ? 'Unlike post' : 'Like post'}>
          <Ionicons name={eng.liked ? 'heart' : 'heart-outline'} size={14} color={eng.liked ? colors.error : colors.muted} />
          <Text style={[styles.postActionText, eng.liked && { color: colors.error }]}>{eng.likes}</Text>
        </TouchableOpacity>
      </View>
    </GlassSurface>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    // No flat bg — the ParticleBackground aurora fills the screen (skill §12).
  },
  loaderBody: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // --- Translucent in-screen header (skill §12) ---
  topBar: {
    // Full-bleed; radius 0 so its bottom edge meets the scrolling banner cleanly.
  },
  topBarTransparent: {
    borderWidth: 0,
    borderBottomWidth: 0,
    shadowOpacity: 0,
    elevation: 0,
    backgroundColor: 'transparent',
  },
  topBarContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm,
    minHeight: 44,
  },
  topBarTitle: {
    flex: 1,
    textAlign: 'center',
    ...typographyScale.h3,
    fontSize: 17,
    letterSpacing: -0.2,
    color: colors.ink,
    ...fonts.semibold,
  },
  topBarSpacer: {
    // Balance the back chevron so the title stays optically centered.
    width: 24,
  },
  errorText: {
    ...typographyScale.h3,
    fontSize: 16,
    color: colors.ink2,
    ...fonts.medium,
  },
  backLink: {
    ...typographyScale.body,
    fontSize: 15,
    color: colors.primary,
    marginTop: spacing.md,
    ...fonts.semibold,
  },
  listContent: {
    paddingBottom: spacing.xxxl,
  },
  bannerContainer: {
    height: 148,
  },
  banner: {
    width: '100%',
    height: '100%',
  },
  profileSection: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
  },
  avatarActionsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginTop: -36,
  },
  avatar: {
    width: 72,
    height: 72,
    borderRadius: 36,
    borderWidth: 3,
    borderColor: colors.white,
    backgroundColor: colors.faint,
    ...shadowTokens.card,
  },
  avatarFallback: {
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarLetter: {
    color: colors.white,
    fontSize: 22,
    ...fonts.bold,
  },
  postAvatarLetter: {
    color: colors.white,
    fontSize: 12,
    ...fonts.bold,
  },
  // Live online dot, bottom-right of the 72pt profile avatar.
  profileOnlineDot: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.success,
    borderWidth: 3,
    borderColor: colors.white,
  },
  presenceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  presenceDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  presenceText: {
    ...typographyScale.caption,
    fontSize: 13,
    color: colors.muted,
    ...fonts.medium,
  },
  // Tighter gap (xs) so five actions — more / message / audio call / video call
  // / follow — fit beside the 72pt avatar on 375pt-wide phones.
  actionButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingTop: 40,
  },
  // Circular glass icon buttons (more / message / audio / video).
  iconBtn: {
    width: 36,
    height: 36,
  },
  iconBtnContent: {
    width: 36,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  callBtnDisabled: {
    opacity: glassTokens.disabledOpacity,
  },
  followBtn: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.sm + 2,
    backgroundColor: colors.primary,
    borderRadius: radius.full,
    ...shadowTokens.bar,
  },
  followingBtn: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.primary,
    shadowOpacity: 0,
    elevation: 0,
  },
  followBtnText: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.white,
    ...fonts.semibold,
  },
  followingBtnText: {
    color: colors.primary,
  },
  // "Edit Profile" (own-profile via the UserProfile route) — outlined pill in the
  // same slot as Follow, matching the Following (secondary) treatment.
  editProfileBtn: {
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.sm + 2,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: colors.primary,
    borderRadius: radius.full,
  },
  editProfileBtnText: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.primary,
    ...fonts.semibold,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.md,
  },
  displayName: {
    ...typographyScale.h2,
    fontSize: 22,
    letterSpacing: -0.4,
    color: colors.ink,
    ...fonts.bold,
  },
  username: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.muted,
    marginTop: 2,
    ...fonts.regular,
  },
  bio: {
    ...typographyScale.body,
    fontSize: 14,
    lineHeight: 20,
    color: colors.ink,
    marginTop: spacing.md,
    ...fonts.regular,
  },
  joinedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.md,
  },
  joinedText: {
    ...typographyScale.caption,
    fontSize: 13,
    color: colors.muted,
    ...fonts.regular,
  },
  statsRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.lg,
  },
  statItem: {
    flex: 1,
  },
  statContent: {
    paddingVertical: spacing.sm + 2,
    paddingHorizontal: spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  statNumber: {
    ...typographyScale.h3,
    fontSize: 17,
    color: colors.ink,
    ...fonts.bold,
    ...tabularNums,
  },
  statLabel: {
    ...typographyScale.caption,
    fontSize: 12,
    color: colors.muted,
    ...fonts.regular,
  },
  postsHeader: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xs,
  },
  postsHeaderText: {
    ...typographyScale.h3,
    fontSize: 17,
    letterSpacing: -0.2,
    color: colors.ink,
    ...fonts.bold,
  },
  postCard: {
    marginHorizontal: spacing.lg,
    marginTop: spacing.md,
  },
  postCardContent: {
    padding: spacing.lg,
  },
  postCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  postAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.faint,
    marginRight: spacing.sm,
  },
  postName: {
    ...typographyScale.body,
    fontSize: 14,
    color: colors.ink,
    ...fonts.semibold,
  },
  postHandle: {
    ...typographyScale.caption,
    fontSize: 12,
    color: colors.muted,
    ...fonts.regular,
  },
  postTime: {
    ...typographyScale.caption,
    fontSize: 12,
    color: colors.faint,
    ...fonts.regular,
  },
  postContent: {
    ...typographyScale.body,
    fontSize: 15,
    lineHeight: 21,
    color: colors.ink,
    ...fonts.regular,
  },
  postActions: {
    flexDirection: 'row',
    gap: spacing.xl,
    marginTop: spacing.md,
  },
  postActionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  postActionText: {
    ...typographyScale.caption,
    fontSize: 12,
    color: colors.muted,
    ...fonts.regular,
    ...tabularNums,
  },
  emptyText: {
    ...typographyScale.body,
    textAlign: 'center',
    color: colors.muted,
    fontSize: 14,
    marginTop: spacing.xxxl,
    ...fonts.regular,
  },
});
